import _ from "lodash";
import uniq from "lodash/uniq";

import { ITableSorting } from "components/Table/CustomTable/CustomTable";
import {
  IArchiveActivitySuccessActionCreator,
  IArchiveWorkflowSuccessActionCreator,
  ICreateActivitySuccessActionCreator,
  ICreateWorkflowSuccessActionCreator,
  IDeleteActivityReportSuccessActionCreator,
  IDeleteActivitySuccessActionCreator,
  IDeleteWorkflowSuccessActionCreator,
  IEditQuestionTagSuccessAction,
  IFetchActivityReportsSuccessActionCreator,
  IFetchActivityVersionSchemaSuccessActionCreator,
  IFetchAllActivitiesSuccessActionCreator,
  IFetchAllWorkflowsSuccessActionCreator,
  IFetchAugmentedActivityReportsSuccessActionCreator,
  IFetchJobLogsSuccessActionCreator,
  IFetchWorkflowReportsSuccessActionCreator,
  IFetchWorkflowReportSuccessActionCreator,
  IReorderWorkflowSuccessActionCreator,
  IRestoreActivitySuccessActionCreator,
  IRestoreWorkflowSuccessActionCreator,
  IResumeWorkflowReportExecutionSuccessActionCreator,
  ISetActivityReportSortingAction,
  IUpdateActivityReportSuccessActionCreator,
  IUpdateActivitySuccessActionCreator,
  IUpdateWorkflowLastReportSuccessActionCreator,
  IUpdateWorkflowSuccessActionCreator,
} from "containers/workflows/redux/actionCreators";
import { STEP_TYPE } from "model/entities/Job";
import {
  IActivity,
  IActivityReport,
  IStepOverview,
  IWorkflow,
  IWorkflowReportWithStepOverview,
  STEP_STATUS,
} from "model/entities/Workflow";
import { IAction, IActionError } from "redux/store/model";
import {
  convertArrayToObject,
  extractIdsFromArray,
  insertNewItemToArr,
  removeItem,
  removeObjectFromArray,
  removeObjectFromHash,
  updateErrorsList,
  updateObjectInArray,
} from "utils/reducerUtils";

import {
  ICreateActivityReportSuccessActionCreator,
  ICreateWorkflowReportSuccessActionCreator,
  IDeleteWorkflowReportSuccessActionCreator,
  IStopWorkflowReportExecutionSuccessActionCreator,
} from "./actionCreators";
import * as types from "./actionTypes";
import { IAugmentedActivityReport } from "./api";
import initialState from "./initialState";
import { updateActivityQuestionByTag } from "./utils";

export interface WorkflowsState {
  isAttaching: boolean;
  isChangingTag: boolean;
  isFetchingAll: boolean;
  isDeleting: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isArchiving: boolean;
  isRestoring: boolean;
  isCreatingWorkflowReport: boolean;
  isUpdatingWorkflowReport: boolean;
  isDeletingWorkflowReport: boolean;
  isCreatingActivityReport: boolean;
  isUpdatingActivityReport: boolean;
  isDeletingActivityReport: boolean;
  isDownloadingReportRawData: boolean;
  isFetchingActivities: boolean;
  isDeletingActivity: boolean;
  isCreatingActivity: boolean;
  isUpdatingActivity: boolean;
  isUploadingFile: boolean;
  allWorkflows: IWorkflow[];
  allActivities: IActivity[];
  allActivityReports: IActivityReport[];
  allAugmentedActivityReports: IAugmentedActivityReport[];
  isFetchingAugmentedActivityReports: boolean;
  activityReportSorting: ITableSorting;
  byId: { [id: string]: IWorkflow };
  allIds: string[];
  errors: any[];
  lastUpdated: Date;
  isFetchingWorkflowReports: boolean;
  isFetchingJobLogs: boolean;
  allWorkflowReports: IWorkflowReportWithStepOverview[];
  stepsOverview: IStepOverview[];
  totalReportCount: number;
  totalAugmentedReportCount: number;
  isFetchingActivityVersionSchema: boolean;
}

/**
 * Reducer for workflows, handles updating the state of the store in regards to workflows object
 * This will have an initial state that will be updated on every action that is dispatched. if the action
 * is not handled by this reducer, then the initial state will be returned.
 * @param {Object} state Redux store state
 * @param {Object} action Action object
 */
function handleWorkflowCrudActions(state = initialState, action: IAction) {
  switch (action.type) {
    case types.CREATE_WORKFLOW_BEGIN:
      return { ...state, isCreating: true };

    case types.CREATE_WORKFLOW_FAILURE:
      return {
        ...state,
        isCreating: false,
        errors: insertNewItemToArr(
          state.errors,
          (action as IActionError).error,
        ),
      };

    case types.CREATE_WORKFLOW_SUCCESS: {
      const { workflow, files } = action as ICreateWorkflowSuccessActionCreator;
      for (const file of files) {
        const splits = file.file.split("/");
        if (splits[splits.length - 1] === workflow.picture_url) {
          workflow.picture_url = file.url;
        }
      }
      const newAllWorkflows = insertNewItemToArr(state.allWorkflows, workflow);
      const ids = extractIdsFromArray(newAllWorkflows);
      return {
        ...state,
        isCreating: false,
        allWorkflows: newAllWorkflows,
        byId: { ...state.byId, [workflow.id]: workflow },
        allIds: uniq(state.allIds.concat(ids)),
        lastUpdated: new Date(),
      };
    }

    case types.FETCH_ALL_WORKFLOWS_BEGIN:
      return {
        ...state,
        isFetchingAll: true,
        isUpdating: false,
        isArchiving: false,
        isCreating: false,
        isDeleting: false,
        isRestoring: false,
      };

    case types.FETCH_ALL_WORKFLOWS_FAILURE:
      return {
        ...state,
        isFetchingAll: false,
        errors: insertNewItemToArr(
          state.errors,
          (action as IActionError).error,
        ),
      };

    case types.FETCH_ALL_WORKFLOWS_SUCCESS: {
      const { workflows } = action as IFetchAllWorkflowsSuccessActionCreator;
      const ids = extractIdsFromArray(workflows);
      return {
        ...state,
        isFetchingAll: false,
        allWorkflows: workflows,
        byId: convertArrayToObject({}, workflows, "id"),
        allIds: uniq(state.allIds.concat(ids)),
        lastUpdated: new Date(),
      };
    }
    case types.UPDATE_WORKFLOW_BEGIN:
      return { ...state, isUpdating: true };

    case types.UPDATE_WORKFLOW_FAILURE:
      return {
        ...state,
        isUpdating: false,
        errors: insertNewItemToArr(
          state.errors,
          (action as IActionError).error,
        ),
      };

    case types.UPDATE_WORKFLOW_SUCCESS: {
      const { workflow, files } = action as IUpdateWorkflowSuccessActionCreator;
      for (const file of files) {
        const splits = file.file.split("/");
        if (splits[splits.length - 1] === workflow.picture_url) {
          workflow.picture_url = file.url;
        }
      }
      return {
        ...state,
        isUpdating: false,
        allWorkflows: updateObjectInArray(state.allWorkflows, workflow),
        byId: { ...state.byId, [workflow.id]: workflow },
      };
    }

    case types.UPDATE_WORKFLOW_LAST_REPORT: {
      const { workflowId } =
        action as IUpdateWorkflowLastReportSuccessActionCreator;
      const workflowToUpdate = state.allWorkflows.find(
        (workflow) => workflow.id === workflowId,
      );
      const updatedWorkflow = {
        ...workflowToUpdate,
        current_report_summary: {
          ...workflowToUpdate?.current_report_summary,
          status: STEP_STATUS.ONGOING,
        },
      };
      return {
        ...state,
        isUpdating: false,
        allWorkflows: updateObjectInArray(state.allWorkflows, updatedWorkflow),
        byId: { ...state.byId, [workflowId]: updatedWorkflow },
      };
    }

    case types.REORDER_WORKFLOW_SUCCESS: {
      const { workflowsIds } = action as IReorderWorkflowSuccessActionCreator;
      const cloneWorkflows = [...state.allWorkflows];
      const newAllWorkflows = _.map(cloneWorkflows, (workflow) => {
        if (_.includes(workflowsIds, workflow.id)) {
          return { ...workflow, index: _.indexOf(workflowsIds, workflow.id) };
        }
        return workflow;
      });
      return {
        ...state,
        allWorkflows: _.isEmpty(workflowsIds)
          ? cloneWorkflows
          : newAllWorkflows,
      };
    }
    case types.DELETE_WORKFLOW_BEGIN:
      return { ...state, isDeleting: true };

    case types.DELETE_WORKFLOW_FAILURE:
      return {
        ...state,
        isDeleting: false,
        errors: insertNewItemToArr(
          state.errors,
          (action as IActionError).error,
        ),
      };

    case types.DELETE_WORKFLOW_SUCCESS: {
      const { workflowId } = action as IDeleteWorkflowSuccessActionCreator;
      const workflow = state.allWorkflows.find((w) => w.id === workflowId);
      return {
        ...state,
        isDeleting: false,
        allWorkflows: removeObjectFromArray(state.allWorkflows, workflowId),
        byId: removeObjectFromHash(state.byId, workflowId),
        allIds: removeItem(state.allIds, workflow),
      };
    }

    default:
      return null;
  }
}

function handleWorkflowArchiveAndRestoreActions(
  state = initialState,
  action: IAction,
) {
  switch (action.type) {
    case types.ARCHIVE_WORKFLOW_BEGIN:
    case types.ARCHIVE_ACTIVITY_BEGIN:
      return { ...state, isArchiving: true };

    case types.ARCHIVE_WORKFLOW_FAILURE:
    case types.ARCHIVE_ACTIVITY_FAILURE:
      return {
        ...state,
        isArchiving: false,
        errors: insertNewItemToArr(
          state.errors,
          (action as IActionError).error,
        ),
      };

    case types.ARCHIVE_WORKFLOW_SUCCESS: {
      const { workflowId } = action as IArchiveWorkflowSuccessActionCreator;
      const workflow = state.byId[workflowId];
      workflow.active = false;
      return {
        ...state,
        isArchiving: false,
        allWorkflows: updateObjectInArray(state.allWorkflows, workflow),
        byId: { ...state.byId, [workflow.id]: workflow },
      };
    }

    case types.ARCHIVE_ACTIVITY_SUCCESS: {
      const { activityId } = action as IArchiveActivitySuccessActionCreator;
      const activity = state.allActivities.find((act) => act.id === activityId);
      if (activity) activity.active = false;
      return {
        ...state,
        isArchiving: false,
        allActivities: updateObjectInArray(state.allActivities, activity),
      };
    }

    case types.RESTORE_WORKFLOW_BEGIN:
    case types.RESTORE_ACTIVITY_BEGIN:
      return { ...state, isRestoring: true };

    case types.RESTORE_WORKFLOW_FAILURE:
    case types.RESTORE_ACTIVITY_FAILURE:
      return {
        ...state,
        isRestoring: false,
        errors: insertNewItemToArr(
          state.errors,
          (action as IActionError).error,
        ),
      };

    case types.RESTORE_WORKFLOW_SUCCESS: {
      const { workflowId } = action as IRestoreWorkflowSuccessActionCreator;
      const workflow = state.byId[workflowId];
      workflow.active = true;
      return {
        ...state,
        isRestoring: false,
        allWorkflows: updateObjectInArray(state.allWorkflows, workflow),
        byId: { ...state.byId, [workflow.id]: workflow },
      };
    }

    case types.RESTORE_ACTIVITY_SUCCESS: {
      const { activityId } = action as IRestoreActivitySuccessActionCreator;
      const activity = state.allActivities.find((act) => act.id === activityId);
      if (activity) {
        activity.active = true;
        return {
          ...state,
          isRestoring: false,
          allActivities: updateObjectInArray(state.allActivities, activity),
          byId: { ...state.byId, [activity.id]: activity },
        };
      }
      return { ...state };
    }

    default:
      return null;
  }
}

function handleActivityCrudActions(
  state = initialState,
  action: IAction,
): WorkflowsState | null {
  switch (action.type) {
    case types.CREATE_ACTIVITY_BEGIN:
      return { ...state, isCreatingActivity: true };

    case types.CREATE_ACTIVITY_FAILURE:
      return {
        ...state,
        isCreatingActivity: false,
        errors: insertNewItemToArr(
          state.errors,
          (action as IActionError).error,
        ),
      };

    case types.CREATE_ACTIVITY_SUCCESS: {
      const { activity } = action as ICreateActivitySuccessActionCreator;
      return {
        ...state,
        isCreatingActivity: false,
        allActivities: insertNewItemToArr(state.allActivities, activity),
        lastUpdated: new Date(),
      };
    }

    case types.FETCH_ALL_ACTIVITIES_BEGIN:
      return {
        ...state,
        isFetchingActivities: true,
        isUpdatingActivity: false,
        isCreatingActivity: false,
        isDeletingActivity: false,
      };

    case types.FETCH_ALL_ACTIVITIES_FAILURE:
      return {
        ...state,
        isFetchingActivities: false,
        errors: insertNewItemToArr(
          state.errors,
          (action as IActionError).error,
        ),
      };

    case types.FETCH_ALL_ACTIVITIES_SUCCESS: {
      const { activities } = action as IFetchAllActivitiesSuccessActionCreator;
      return {
        ...state,
        isFetchingActivities: false,
        allActivities: activities,
        lastUpdated: new Date(),
      };
    }

    case types.UPDATE_ACTIVITY_BEGIN:
      return { ...state, isUpdatingActivity: true };

    case types.UPDATE_ACTIVITY_FAILURE:
      return {
        ...state,
        isUpdatingActivity: false,
        errors: insertNewItemToArr(
          state.errors,
          (action as IActionError).error,
        ),
      };

    case types.UPDATE_ACTIVITY_SUCCESS: {
      const { activity } = action as IUpdateActivitySuccessActionCreator;
      return {
        ...state,
        isUpdatingActivity: false,
        allActivities: updateObjectInArray(state.allActivities, activity),
      };
    }

    case types.DELETE_ACTIVITY_BEGIN:
      return { ...state, isDeletingActivity: true };

    case types.DELETE_ACTIVITY_FAILURE:
      return {
        ...state,
        isDeletingActivity: false,
        errors: insertNewItemToArr(
          state.errors,
          (action as IActionError).error,
        ),
      };

    case types.DELETE_ACTIVITY_SUCCESS: {
      const { activityId } = action as IDeleteActivitySuccessActionCreator;
      return {
        ...state,
        isDeletingActivity: false,
        allActivities: removeObjectFromArray(state.allActivities, activityId),
      };
    }

    case types.CHANGE_LIST_QUESTION_TAG_BEGIN:
      return { ...state, isChangingTag: true };

    case types.CHANGE_LIST_QUESTION_TAG_FAILURE:
      return {
        ...state,
        isChangingTag: false,
        errors: updateErrorsList(state, action),
      };

    case types.CHANGE_LIST_QUESTION_TAG_SUCCESS: {
      const { params, activityId } = action as IEditQuestionTagSuccessAction;
      const { new_tag, old_tag, matrix_tag: parent } = params;
      let selectedActivity = _.find([...state.allActivities], {
        id: activityId,
      });
      if (selectedActivity) {
        selectedActivity = updateActivityQuestionByTag(
          selectedActivity,
          new_tag,
          old_tag,
          parent,
        );
      }
      return {
        ...state,
        allActivities: updateObjectInArray(
          state.allActivities,
          selectedActivity,
        ),
        isChangingTag: false,
      };
    }

    default:
      return null;
  }
}

function handleWorkflowReportActions(
  state = initialState,
  action: IAction,
): WorkflowsState | null {
  switch (action.type) {
    case types.CREATE_WORKFLOW_REPORT_BEGIN:
      return { ...state, isCreatingWorkflowReport: true };

    case types.CREATE_WORKFLOW_REPORT_FAILURE:
      return {
        ...state,
        isCreatingWorkflowReport: false,
        errors: insertNewItemToArr(
          state.errors,
          (action as IActionError).error,
        ),
      };

    case types.CREATE_WORKFLOW_REPORT_SUCCESS: {
      const { workflow_report } =
        action as ICreateWorkflowReportSuccessActionCreator;
      return {
        ...state,
        isCreatingWorkflowReport: false,
        allWorkflowReports: insertNewItemToArr(
          state.allWorkflowReports,
          workflow_report,
        ),
      };
    }

    case types.FETCH_WORKFLOW_REPORTS_BEGIN:
      return { ...state, isFetchingWorkflowReports: true };

    case types.FETCH_WORKFLOW_REPORTS_FAILURE:
      return {
        ...state,
        isFetchingWorkflowReports: false,
        errors: insertNewItemToArr(
          state.errors,
          (action as IActionError).error,
        ),
      };

    case types.FETCH_WORKFLOW_REPORTS_SUCCESS: {
      const { workflow_reports } =
        action as IFetchWorkflowReportsSuccessActionCreator;
      return {
        ...state,
        isFetchingWorkflowReports: false,
        allWorkflowReports: workflow_reports,
      };
    }

    case types.FETCH_WORKFLOW_REPORT_BEGIN:
      return { ...state, isFetchingWorkflowReports: true };

    // eslint-disable-next-line sonarjs/no-duplicated-branches
    case types.FETCH_WORKFLOW_REPORT_FAILURE:
      return {
        ...state,
        isFetchingWorkflowReports: false,
        errors: insertNewItemToArr(
          state.errors,
          (action as IActionError).error,
        ),
      };

    case types.FETCH_WORKFLOW_REPORT_SUCCESS: {
      const { workflow_report } =
        action as IFetchWorkflowReportSuccessActionCreator;
      let allWorkflowReports = state.allWorkflowReports;
      if (workflow_report) {
        const reportIndex = _.findIndex(state.allWorkflowReports, {
          id: workflow_report.id,
        });
        allWorkflowReports =
          reportIndex === -1
            ? [...allWorkflowReports, workflow_report]
            : _.map(allWorkflowReports, (report) =>
                report.id === workflow_report.id ? workflow_report : report,
              );
      }
      return { ...state, isFetchingWorkflowReports: false, allWorkflowReports };
    }

    case types.STOP_WORKFLOW_REPORT_EXECUTION_BEGIN:
    case types.RESUME_WORKFLOW_REPORT_EXECUTION_BEGIN:
    case types.UPDATE_WORKFLOW_REPORT_BEGIN:
      return { ...state, isUpdatingWorkflowReport: true };

    case types.UPDATE_WORKFLOW_REPORT_FAILURE:
    case types.STOP_WORKFLOW_REPORT_EXECUTION_FAILURE:
    case types.RESUME_WORKFLOW_REPORT_EXECUTION_FAILURE:
      return {
        ...state,
        isUpdatingWorkflowReport: false,
        errors: insertNewItemToArr(
          state.errors,
          (action as IActionError).error,
        ),
      };

    case types.UPDATE_WORKFLOW_REPORT_SUCCESS: {
      const { workflow_report } =
        action as ICreateWorkflowReportSuccessActionCreator;
      return {
        ...state,
        isUpdatingWorkflowReport: false,
        allWorkflowReports: updateObjectInArray(
          state.allWorkflowReports,
          workflow_report,
        ),
      };
    }

    case types.STOP_WORKFLOW_REPORT_EXECUTION_SUCCESS:
    case types.RESUME_WORKFLOW_REPORT_EXECUTION_SUCCESS: {
      const { workflow_report } = action as
        | IStopWorkflowReportExecutionSuccessActionCreator
        | IResumeWorkflowReportExecutionSuccessActionCreator;
      return {
        ...state,
        isUpdatingWorkflowReport: false,
        allWorkflowReports: state.allWorkflowReports.map((rep) =>
          rep.id === workflow_report.id
            ? { ...rep, status: workflow_report.status }
            : rep,
        ),
      };
    }

    case types.DELETE_WORKFLOW_REPORT_BEGIN:
      return { ...state, isDeletingWorkflowReport: true };

    case types.DELETE_WORKFLOW_REPORT_FAILURE:
      return {
        ...state,
        isDeletingWorkflowReport: false,
        errors: insertNewItemToArr(
          state.errors,
          (action as IActionError).error,
        ),
      };

    case types.DELETE_WORKFLOW_REPORT_SUCCESS: {
      const { workflowReportId } =
        action as IDeleteWorkflowReportSuccessActionCreator;
      return {
        ...state,
        isDeletingWorkflowReport: false,
        allWorkflowReports: state.allWorkflowReports.filter(
          (wr) => wr.id !== workflowReportId,
        ),
      };
    }

    case types.DOWNLOAD_REPORT_RAW_DATA_BEGIN:
      return { ...state, isDownloadingReportRawData: true };

    case types.DOWNLOAD_REPORT_RAW_DATA_FAILURE:
      return {
        ...state,
        isDownloadingReportRawData: false,
        errors: insertNewItemToArr(
          state.errors,
          (action as IActionError).error,
        ),
      };

    case types.DOWNLOAD_REPORT_RAW_DATA_SUCCESS:
      return { ...state, isDownloadingReportRawData: false };

    default:
      return null;
  }
}

function handleActivityReportActions(state = initialState, action: IAction) {
  switch (action.type) {
    case types.CREATE_ACTIVITY_REPORT_BEGIN:
      return { ...state, isCreatingActivityReport: true };

    case types.CREATE_ACTIVITY_REPORT_FAILURE:
      return {
        ...state,
        isCreatingActivityReport: false,
        errors: insertNewItemToArr(
          state.errors,
          (action as IActionError).error,
        ),
      };

    case types.CREATE_ACTIVITY_REPORT_SUCCESS: {
      const { activity_report } =
        action as ICreateActivityReportSuccessActionCreator;
      const allActivityReports = state.allActivityReports.map((report) =>
        report._id === activity_report._id ? activity_report : report,
      );
      const wr = state.allWorkflowReports.find(
        (wr) => wr.id === activity_report.workflow_report_id,
      );
      (wr as any).steps = [
        {
          id: activity_report.id,
          started_at: activity_report.started_at,
          status: STEP_STATUS.DONE,
          type: STEP_TYPE.ACTIVITY,
          workflow_id: activity_report.workflow_id,
          schema_id: activity_report.activity_id,
          step_id: activity_report.step_id,
          workflow_report_id: activity_report.workflow_report_id,
        },
      ];
      return {
        ...state,
        isCreatingActivityReport: false,
        allActivityReports,
        allWorkflowReports: updateObjectInArray(state.allWorkflowReports, wr),
      };
    }

    case types.FETCH_ACTIVITY_REPORTS_BEGIN:
      return { ...state };

    case types.FETCH_ACTIVITY_REPORTS_FAILURE:
      return {
        ...state,
        errors: insertNewItemToArr(
          state.errors,
          (action as IActionError).error,
        ),
      };

    case types.FETCH_ACTIVITY_REPORTS_SUCCESS: {
      const { reports, totalCount, appendMode } =
        action as IFetchActivityReportsSuccessActionCreator;
      let allActivityReports = reports;
      if (appendMode) {
        allActivityReports = _.reduce(
          state.allActivityReports,
          (sum, n) => {
            return sum.map((e) => e.metadata._id).includes(n.metadata._id)
              ? sum
              : [...sum, n];
          },
          reports,
        );
      }
      return { ...state, allActivityReports, totalReportCount: totalCount };
    }

    case types.FETCH_AUGMENTED_ACTIVITY_REPORTS_BEGIN:
      return { ...state, isFetchingAugmentedActivityReports: true };

    case types.FETCH_AUGMENTED_ACTIVITY_REPORTS_FAILURE:
      return {
        ...state,
        isFetchingAugmentedActivityReports: false,
        errors: insertNewItemToArr(
          state.errors,
          (action as IActionError).error,
        ),
      };

    case types.FETCH_AUGMENTED_ACTIVITY_REPORTS_SUCCESS: {
      const { reports, totalCount, options } =
        action as IFetchAugmentedActivityReportsSuccessActionCreator;
      const { appendMode, clearExistingTableReports } = options ?? {};
      let allAugmentedActivityReports = reports;

      if (!clearExistingTableReports && appendMode) {
        allAugmentedActivityReports = _.concat(
          state.allAugmentedActivityReports,
          reports,
        );
      }
      return {
        ...state,
        allAugmentedActivityReports,
        totalAugmentedReportCount: totalCount,
        isFetchingAugmentedActivityReports: false,
      };
    }

    case types.UPDATE_ACTIVITY_REPORT_BEGIN:
      return { ...state, isUpdatingActivityReport: true };

    case types.UPDATE_ACTIVITY_REPORT_FAILURE:
      return {
        ...state,
        isUpdatingActivityReport: false,
        errors: insertNewItemToArr(
          state.errors,
          (action as IActionError).error,
        ),
      };

    case types.UPDATE_ACTIVITY_REPORT_SUCCESS: {
      const { activity_report } =
        action as IUpdateActivityReportSuccessActionCreator;
      return {
        ...state,
        isUpdatingActivityReport: false,
        allActivityReports: _.map(state.allActivityReports, (report) =>
          report.metadata._id === activity_report.metadata._id
            ? activity_report
            : report,
        ),
        allAugmentedActivityReports: _.map(
          state.allAugmentedActivityReports,
          (report) =>
            report.metadata._id === activity_report.metadata._id
              ? activity_report
              : report,
        ),
      };
    }

    case types.DELETE_ACTIVITY_REPORT_BEGIN:
      return { ...state, isDeletingActivityReport: true };

    case types.DELETE_ACTIVITY_REPORT_FAILURE:
      return {
        ...state,
        isDeletingActivityReport: false,
        errors: insertNewItemToArr(
          state.errors,
          (action as IActionError).error,
        ),
      };

    case types.DELETE_ACTIVITY_REPORT_SUCCESS: {
      const { reportId } = action as IDeleteActivityReportSuccessActionCreator;
      return {
        ...state,
        isDeletingActivityReport: false,
        allActivityReports: _.filter(
          state.allActivityReports,
          (report) => report._id !== reportId,
        ),
        allAugmentedActivityReports: _.filter(
          state.allAugmentedActivityReports,
          (report) => report.metadata._id !== reportId,
        ),
      };
    }

    case types.RESET_ACTIVITY_REPORT_SELECTION:
      return { ...state, allActivityReports: [] };

    case types.SET_ACTIVITY_REPORT_SORTING: {
      const { sorting } = action as ISetActivityReportSortingAction;
      return { ...state, activityReportSorting: sorting };
    }

    default:
      return null;
  }
}

function handleWorkflowMiscActions(state = initialState, action: IAction) {
  switch (action.type) {
    case types.UPLOAD_FILE_BEGIN:
      return { ...state, isUploadingFile: true };

    case types.UPLOAD_FILE_FAILURE:
      return {
        ...state,
        isUploadingFile: false,
        errors: updateErrorsList(state, action),
      };

    case types.UPLOAD_FILE_SUCCESS:
      return { ...state, isUploadingFile: false };

    case types.FETCH_JOB_LOGS_BEGIN:
      return { ...state, isFetchingJobLogs: true };

    case types.FETCH_JOB_LOGS_FAILURE:
      return {
        ...state,
        isFetchingJobLogs: false,
        errors: insertNewItemToArr(
          state.errors,
          (action as IActionError).error,
        ),
      };

    case types.FETCH_JOB_LOGS_SUCCESS: {
      const { steps_overview } = action as IFetchJobLogsSuccessActionCreator;
      return {
        ...state,
        isFetchingJobLogs: false,
        stepsOverview: steps_overview,
      };
    }

    case types.FETCH_ACTIVITY_VERSION_SCHEMA_BEGIN:
      return { ...state, isFetchingActivityVersionSchema: true };

    case types.FETCH_ACTIVITY_VERSION_SCHEMA_FAILURE:
      return {
        ...state,
        isFetchingActivityVersionSchema: false,
        errors: insertNewItemToArr(
          state.errors,
          (action as IActionError).error,
        ),
      };

    case types.FETCH_ACTIVITY_VERSION_SCHEMA_SUCCESS: {
      const { activity } =
        action as IFetchActivityVersionSchemaSuccessActionCreator;
      return {
        ...state,
        isFetchingActivityVersionSchema: false,
        currentActivityReportVersionschema: activity,
      };
    }

    case types.CLEAR_DATA:
      return initialState;

    case types.LOGOUT_REQUEST_SUCCESS:
      return { ...initialState };

    default:
      return null;
  }
}

export default function workflowReducer(state = initialState, action: IAction) {
  return (
    handleWorkflowCrudActions(state, action) ??
    handleWorkflowArchiveAndRestoreActions(state, action) ??
    handleActivityCrudActions(state, action) ??
    handleWorkflowReportActions(state, action) ??
    handleActivityReportActions(state, action) ??
    handleWorkflowMiscActions(state, action) ??
    state
  );
}
